import { useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { adminApi, formatError } from "@/lib/adminApi";
import { useSite } from "@/lib/content";

const input = "w-full rounded-xl border bg-white px-4 py-3 text-base focus:outline-none focus-visible:ring-2 focus-visible:ring-honey";

export function LiveIndicator({ live, testId }) {
  return live ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-[#ECFDF3] px-2.5 py-1 text-xs font-semibold text-[#05603A]" data-testid={testId} data-live="true">
      <Eye className="h-3.5 w-3.5" aria-hidden="true" /> Canlıda görünür
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full bg-stone-100 px-2.5 py-1 text-xs font-semibold text-stone-600" data-testid={testId} data-live="false">
      <EyeOff className="h-3.5 w-3.5" aria-hidden="true" /> Canlıda gizli
    </span>
  );
}

export function Checkbox({ id, label, checked, onChange, testId }) {
  return (
    <label htmlFor={id} className="inline-flex items-center gap-2 min-h-[44px] cursor-pointer text-sm font-medium text-ink">
      <input id={id} type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} className="h-5 w-5 accent-[#1E3A2B]" data-testid={testId} />
      {label}
    </label>
  );
}

export function FieldEditor({ id, label, field, onChange, multiline, placeholder, error, hint }) {
  const filled = field?.value != null && String(field.value).trim() !== "";
  const Tag = multiline ? "textarea" : "input";
  return (
    <div className="rounded-2xl border border-line bg-white p-5 space-y-3" data-testid={`field-${id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <label htmlFor={`f-${id}`} className="text-sm font-semibold text-wood">{label}</label>
        <LiveIndicator live={filled && field.verified} testId={`field-${id}-live`} />
      </div>
      <Tag
        id={`f-${id}`}
        rows={multiline ? 4 : undefined}
        value={field?.value ?? ""}
        placeholder={placeholder}
        onChange={(e) => onChange({ ...field, value: e.target.value })}
        aria-invalid={!!error}
        className={`${input} ${error ? "border-[#B42318]" : "border-line"}`}
        data-testid={`field-${id}-input`}
      />
      {hint && <p className="text-xs text-ink-muted">{hint}</p>}
      {error && <p className="text-sm text-[#B42318]" data-testid={`field-${id}-error`}>{error}</p>}
      <Checkbox id={`v-${id}`} label="Doğrulandı" checked={field?.verified} onChange={(v) => onChange({ ...field, verified: v })} testId={`field-${id}-verified`} />
    </div>
  );
}

export function SectionHeader({ title, text }) {
  return (
    <div className="mb-6">
      <h2 className="text-3xl font-semibold text-forest">{title}</h2>
      {text && <p className="mt-1 text-sm text-ink-muted max-w-2xl">{text}</p>}
    </div>
  );
}

export function useSaver() {
  const { reload } = useSite();
  const [saving, setSaving] = useState(false);
  const save = async (entries) => {
    setSaving(true);
    try {
      for (const [section, payload] of entries) await adminApi.put(`/admin/content/${section}`, payload);
      await reload();
      toast.success("Kaydedildi");
      return true;
    } catch (e) {
      toast.error(formatError(e));
      return false;
    } finally {
      setSaving(false);
    }
  };
  return { save, saving };
}

export function SaveButton({ onClick, saving, testId, label = "Kaydet" }) {
  return (
    <button type="button" onClick={onClick} disabled={saving} className="inline-flex items-center justify-center min-h-[48px] px-8 rounded-full bg-forest text-cream font-semibold hover:bg-forest-dark disabled:opacity-60 transition-colors" data-testid={testId}>
      {saving ? "Kaydediliyor…" : label}
    </button>
  );
}

export function BiFieldEditor({ id, label, field, onChange, multiline, placeholder, hint }) {
  const en = field?.en || { value: null, verified: false };
  const trLive = field?.value != null && String(field.value).trim() !== "" && field.verified;
  return (
    <div className="grid gap-3 md:grid-cols-2 md:col-span-2" data-testid={`bifield-${id}`}>
      <FieldEditor id={id} label={`${label} · TR`} field={field} onChange={(f) => onChange({ ...f, en })} multiline={multiline} placeholder={placeholder} hint={hint} />
      <div className="relative">
        <FieldEditor id={`${id}-en`} label={`${label} · EN`} field={en} onChange={(e) => onChange({ ...field, en: e })} multiline={multiline} hint={!trLive ? "İngilizce metin, Türkçesi canlıda görünür olmadan yayınlanmaz." : undefined} />
      </div>
    </div>
  );
}
